// Co-working text caps + normalization (M55.1, ARCH §30.3, COW-2/COW-3). PURE
// (ENG-1). A payload over its cap is TRUNCATED at the boundary, stored within
// the cap, never refused (E-63) — the same rule for a note, a board line, and
// (already, since M20-ish) a sign.
import { COW } from '../config';
import { hashString } from '../engine/rng';

/** A sticky note's text, capped at `COW.NOTE_MAX` (E-63: truncate, don't refuse). */
export function normalizeNote(text: string): string {
  return text.slice(0, COW.NOTE_MAX);
}

/** One task-board line (`id` added M107.2, ARCH §39.7, save version +1): the stable
 *  identity a linked Hangar Reminders item is mapped to. Assigned by the client at
 *  creation time (`doorId`'s own pattern, `world/doors.ts`) — this module stays PURE
 *  (ENG-1) and never mints one itself. */
export interface TaskLine {
  id: string;
  text: string;
  done: boolean;
}

/**
 * M107.2 — a stable id for board line `index` at `(x,y,z)`: `candidate` if it is
 * already a non-empty string, otherwise a deterministic hash of the position and
 * index. Used both to migrate a pre-id save and to backfill a malformed/legacy
 * edit payload — in both cases the SAME (x,y,z,index) must always produce the SAME
 * id, or a repeated migration (the save never having been re-written to disk) would
 * desync the §39.7 `lineId ↔ reminderItemId` mapping a linked board depends on.
 */
export function stableLineId(x: number, y: number, z: number, index: number, candidate?: unknown): string {
  if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  return `line:${hashString(`${x},${y},${z},${index}`).toString(16)}`;
}

/** A task board's lines, capped at `COW.BOARD_LINES` lines of `COW.LINE_MAX`
 *  chars each (E-63 applies to both bounds independently). */
export function normalizeBoard(lines: readonly TaskLine[]): TaskLine[] {
  return lines
    .slice(0, COW.BOARD_LINES)
    .map((l) => ({ id: l.id, text: l.text.slice(0, COW.LINE_MAX), done: l.done }));
}

/** Append a line to a board (already-normalized), truncating the incoming text
 *  and dropping the line entirely if the board is already at `COW.BOARD_LINES`
 *  — full is full (the same "at the cap" shape `HER.NOTEBOOK_MAX` uses for
 *  animals, minus the eviction: a task board has no "oldest done" analogue).
 *  `id` is supplied by the caller (M107.2) — see `TaskLine`'s own doc comment. */
export function addTask(lines: readonly TaskLine[], text: string, id: string): TaskLine[] {
  if (lines.length >= COW.BOARD_LINES) return [...lines];
  return [...lines, { id, text: text.slice(0, COW.LINE_MAX), done: false }];
}

/** Toggle one line's `done`, or the same array back if the index is out of range. */
export function toggleTask(lines: readonly TaskLine[], index: number): TaskLine[] {
  if (index < 0 || index >= lines.length) return [...lines];
  return lines.map((l, i) => (i === index ? { ...l, done: !l.done } : { ...l }));
}

/** Remove one line by index, or the same array back if out of range. */
export function deleteTask(lines: readonly TaskLine[], index: number): TaskLine[] {
  if (index < 0 || index >= lines.length) return [...lines];
  return lines.filter((_, i) => i !== index);
}
